import React, { useState } from "react";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { MaterialReactTable } from "material-react-table";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import isMock from "../../config";
import {useMaterialUIController} from "../../context";

// Recursive search function for nested filtering (same as your code)
const searchRowRecursively = (row, columnId, filterValue) => {
    const value = row.getValue(columnId) || "";
    if (value.toString().toLowerCase().includes(filterValue.toLowerCase())) {
        return true;
    }
    if (row.subRows?.length) {
        return row.subRows.some((subRow) => searchRowRecursively(subRow, columnId, filterValue));
    }
    return false;
};

function Users() {
    // State to manage car-dialog
    const [carsDialogOpen, setCarsDialogOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [controller] = useMaterialUIController();

    const { darkMode } = controller;

    // Fetch users with React Query
    const {
        data: users = [],
        isLoading,
        error,
        refetch,
    } = useQuery({
        queryKey: ["users"],
        queryFn: async () => {
            const response = await fetch("http://localhost:3001/users");
            if (!response.ok) {
                throw new Error("Failed to fetch users");
            }
            const data = await response.json();
            // For non-mock, structure is data.parkingUsers
            console.log(data);
            return isMock ? data : data.parkingUsers || [];
        },
    });

    // Delete handler
    const handleDeleteUser = async (userId) => {
        if (!window.confirm("Are you sure you want to delete this user?")) {
            return;
        }
        try {
            const response = await fetch(`http://localhost:3001/users/${userId}`, {
                method: "DELETE",
            });
            if (!response.ok) {
                throw new Error("Failed to delete user");
            }
            alert("User deleted successfully");
            refetch(); // Re-fetch so table updates
        } catch (err) {
            alert(`Error: ${err.message}`);
        }
    };

    // When you click the “Cars” cell
    const handleShowCars = (user) => {
        setSelectedUser(user);
        setCarsDialogOpen(true);
    };

    // Define table columns
    const columns = [
        {
            header: "Name",
            // Combine firstName + lastName
            Cell: ({ row }) => {
                const { firstName, lastName } = row.original;
                return `${firstName ?? ""} ${lastName ?? ""}`.trim();
            },
        },
        {
            accessorKey: "phoneNumber",
            header: "Phone Number",
        },
        {
            accessorKey: "carLimit",
            header: "Car Limit",
        },
        {
            header: "Cars",
            Cell: ({ row }) => {
                const { cars } = row.original;
                const count = cars?.length || 0;
                const label = count === 1 ? "1 Car" : `${count} Cars`;
                return (
                    <span
                        style={{ color: "#1976d2", textDecoration: "underline", cursor: "pointer" }}
                        onClick={() => handleShowCars(row.original)}
                    >
            {label}
          </span>
                );
            },
        },
        {
            header: "Actions",
            Cell: ({ row }) => {
                const userId = row.original.id;
                return (
                    <Button
                        style={{ color: "white", backgroundColor: "red", cursor: "pointer" }}
                        onClick={() => handleDeleteUser(userId)}
                    >
                        Delete
                    </Button>
                );
            },
        },
    ];

    // MUI Theme customization
    const theme = createTheme({
        components: {
            MuiTablePagination: {
                styleOverrides: {
                    root: { backgroundColor: "#f5f5f5", color: "#000" },
                    selectIcon: { color: "#000" },
                },
            },
            MuiSvgIcon: { styleOverrides: { root: { color: "#000" } } },
            MuiInputBase: { styleOverrides: { root: { color: "#000" } } },
        },
    });

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={6} pb={3} px={2}>
                <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                    <MDTypography variant="h4">Users</MDTypography>
                </MDBox>

                {isLoading ? (
                    <MDTypography variant="h6">Loading users...</MDTypography>
                ) : error ? (
                    <MDTypography variant="h6" color="error">
                        Error: {error.message}
                    </MDTypography>
                ) : (
                    <ThemeProvider theme={theme}>
                        <MaterialReactTable
                            columns={columns}
                            data={users}
                            enableGlobalFilter
                            globalFilterFn="recursiveSearch"
                            filterFns={{
                                recursiveSearch: (row, columnId, filterValue) =>
                                    searchRowRecursively(row, columnId, filterValue),
                            }}
                            muiTableHeadProps={{
                                sx: {
                                    display: "table-header-group",
                                    backgroundColor: "rgb(0,255,234)",
                                    color: "white",
                                },
                            }}

                            //
                            muiBottomToolbarProps={{
                                sx: {
                                    backgroundColor: darkMode ? '#1e293b' : '#fff',
                                    color: darkMode ? '#fff' : '#1e293b',
                                    ".MuiFormLabel-root": {
                                        color: darkMode ? '#fff' : '#1e293b',
                                    },
                                    ".MuiSelect-icon": {
                                        color: darkMode ? '#fff' : '#1e293b',
                                    },
                                    ".MuiSelect-select": {
                                        color: darkMode ? '#fff' : '#1e293b',
                                    },
                                    ".MuiBox-root.MuiButtonBase-root" : {
                                        color: darkMode ? '#fff' : '#1e293b',
                                    },
                                    ".MuiSvgIcon-root" : {
                                        color: darkMode ? '#fff' : '#1e293b',
                                    },
                                    '& .MuiButtonBase-root.Mui-disabled .MuiSvgIcon-root': {
                                        // pick a dimmer color for disabled icons
                                        color: darkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
                                    },
                                }
                            }}
                            muiTopToolbarProps={{
                                sx: {
                                    backgroundColor: darkMode ? '#1e293b' : '#fff',
                                    color: '#fff',
                                    ".MuiSvgIcon-root" : {

                                        color: darkMode ? '#fff' : '#1e293b',

                                    },
                                    ".MuiTableSortLabel-iconDirectionAsc": {
                                        color: darkMode ? '#fff' : '#1e293b',
                                    },
                                    '& .MuiInputBase-input': {
                                        color: darkMode ? '#fff' : '#1e293b',
                                    }
                                },

                            }}
                            muiTableBodyCellProps={{
                                sx: {
                                    backgroundColor: darkMode ? "#1e293b" : "#ffffff",
                                    color: darkMode ? "#ffffff" : "#000",
                                    ".MuiSvgIcon-root" : {
                                        color: darkMode ? "#ffffff" : "#000",
                                    }
                                },
                            }}
                            muiTableHeadCellProps={{
                                sx: {
                                    backgroundColor: darkMode ? "#1e293b" : "#0098CF",
                                    color: "white",
                                    fontWeight: "bold",
                                    ".MuiSvgIcon-root" : {
                                        color: darkMode ? "#ffffff" : "#000",
                                    },
                                    // then specifically target the sort label icon:
                                    '& .MuiTableSortLabel-icon': {
                                        color: darkMode ? '#fff !important' : '#000 !important',
                                    },
                                    // likewise for iconDirectionAsc / iconDirectionDesc if needed
                                    '& .MuiTableSortLabel-iconDirectionAsc': {
                                        color: darkMode ? '#fff !important' : '#000 !important',
                                    },
                                    '& .MuiTableSortLabel-iconDirectionDesc': {
                                        color: darkMode ? '#fff !important' : '#000 !important',
                                    },
                                    '& .MuiButtonBase-root.Mui-disabled .MuiSvgIcon-root': {
                                        // pick a dimmer color for disabled icons
                                        color: darkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
                                    },
                                    '& .MuiInputBase-input': {
                                        color: darkMode ? '#fff' : '#1e293b',
                                    }
                                },
                            }}
                            //
                            muiTableContainerProps={{
                                sx: {
                                    width: "100%",
                                    overflowX: "auto",
                                    backgroundColor: "#15aff4",
                                    color: "white",
                                },
                            }}
                        />
                    </ThemeProvider>
                )}
            </MDBox>
            <Footer />

            {/* Dialog to display cars (license plates) for selectedUser */}
            <Dialog open={carsDialogOpen} onClose={() => setCarsDialogOpen(false)} fullWidth maxWidth="xs">
                <DialogTitle sx={{color: "#000"}}>Cars</DialogTitle>
                <DialogContent sx={{color: "#000"}}>
                    {selectedUser?.cars && selectedUser.cars.length > 0 ? (
                        selectedUser.cars.map((car, idx) => (
                            <div key={idx} style={{ marginBottom: "8px" }}>
                                • {car.licensePlate}
                            </div>
                        ))
                    ) : (
                        <div>No cars</div>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCarsDialogOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        </DashboardLayout>
    );
}

export default Users;
